import { AxiosRequestConfig } from "axios";
import useAxios from "axios-hooks";

import { useEffect, useMemo, useState } from "react";
import API from "constants/api";
import { BUYER } from "constants/global";
import { Country, FilterOptions } from "types/global";
import { ApplicableTestLevel, ExistingSku, SkuGroupingPayload } from "types/sku-group";
import { CustomHeader } from "types/task";
import { useErrorAlert, useSuccessAlert } from "./useAlert";
import { useUserRole } from "./useUserRole";

function useFetchSkuGrouping<T>({
  method = "GET",
  api = API.SKU_GROUPING,
  id = "",
  url = "",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
}) {
  const { setError } = useErrorAlert();

  const { isBuyer } = useUserRole();

  const [data, setData] = useState<T>();

  const PATH = isBuyer ? `/${BUYER}${api}` : api;

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !id && !url ? PATH : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data: data as T,
      isLoading,
      refetch,
      error,
    }),
    [id, url, data, isLoading],
  );
}

function useUpdateSkuGrouping<Req, Res = { id?: string }>({
  method = "POST",
  api = API.SKU_GROUPING,
  id = "",
  url = "",
  successAlertEnabled = false,
  successMessage = "Submitted successfully.",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
  successAlertEnabled?: boolean;
  successMessage?: string;
}) {
  const { setError, setAppError } = useErrorAlert();
  const { setSuccess } = useSuccessAlert();

  const { isBuyer } = useUserRole();

  const PATH = isBuyer ? `/${BUYER}${api}` : api;

  const [data, setData] = useState<Res>();

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !id && !url ? PATH : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.code !== 200) {
      setAppError({
        code: fetchData?.code,
        message: fetchData?.message,
      });
    }

    if (successAlertEnabled && fetchData?.code === 200) setSuccess(successMessage);

    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data,
      isLoading,
      refetch: (payload: Req) => refetch({ data: payload }),
      error,
    }),
    [api, id, url, successAlertEnabled, successMessage, data, isLoading],
  );
}

export function useCreateSkuGrouping<T = SkuGroupingPayload>() {
  return useUpdateSkuGrouping<T>({
    successAlertEnabled: true,
    successMessage:
      "SKU grouping created successfully and the test request has been sent to Vendor.",
  });
}

export function useEditSkuGrouping<T = SkuGroupingPayload>(taskId: string) {
  return useUpdateSkuGrouping<T>({
    method: "PUT",
    id: taskId,
    successAlertEnabled: true,
    successMessage:
      "SKU grouping updated successfully and the revised test request has been sent to Vendor",
  });
}

export function useExistingSkuList<Req = FilterOptions, Res = ExistingSku[]>() {
  return useUpdateSkuGrouping<Req, Res>({
    url: "new/skus/search",
  });
}

export function useEditExistingSkuList<Req = FilterOptions, Res = ExistingSku[]>() {
  return useUpdateSkuGrouping<Req, Res>({
    url: "edit/skus/search",
  });
}

export function useExistingSkuListFilters<Req = FilterOptions, Res = CustomHeader>() {
  return useUpdateSkuGrouping<Req, Res>({
    url: "new/skus/filter_options",
  });
}

export function useEditExistingSkuListFilters<Req = FilterOptions, Res = CustomHeader>() {
  return useUpdateSkuGrouping<Req, Res>({
    url: "edit/skus/filter_options",
  });
}

export function useGroupingBrandOptions<Req = { countries: Country[] }>() {
  return useUpdateSkuGrouping<Req>({
    url: "brands",
  });
}

export function useGroupingSubcategoryOptions<Req = { countries: Country[]; brand: string }>() {
  return useUpdateSkuGrouping<Req>({
    url: "sub_categories",
  });
}

// get applicable test matrix by brand & sub-category
export function useGroupingApplicableTestLevel<
  Req = { brand: string; subCategory: string },
  Res = ApplicableTestLevel,
>() {
  return useUpdateSkuGrouping<Req, Res>({
    url: "applicable_test_level",
  });
}
